import {
  CurrencySettleOrderTokenType,
  CurrencyTokenType,
  useCurrencyContext
} from '@apeiron/currency'
import { NextImage, noForwardProps } from '@apeiron/temp'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, ButtonBase, styled, Typography } from '@mui/material'
import PriceText from '@src/components/share/generic/PriceText'
import ExchangeRateDetail from '@src/components/share/trade/buyDialogView/ExchangeRateDetail'
import { EXCHANGE_FEE_MULTIPLIER } from '@src/constants/marketplaceOrder'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const OPTIONS: CurrencySettleOrderTokenType[] = [
  CurrencyTokenType.Weth,
  CurrencyTokenType.Aprs,
  CurrencyTokenType.Anima,
  CurrencyTokenType.Ron,
  CurrencyTokenType.Usdc
]

const CurrencyIconImage = styled(NextImage)`
  width: 20px;
  aspect-ratio: 1;
`

const CurrentCurrencyIconImage = styled(NextImage)`
  width: 32px;
  aspect-ratio: 1;
`

const Container = styled(Box)`
  border-radius: 6px;
  background-color: #000;
  padding: 6px;
  width: 100%;
`

const SwitcherContainer = styled(Box)`
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #151615;
  display: flex;
  padding: 8px 12px;
  justify-content: space-around;
`

const SwitchButton = styled(ButtonBase, noForwardProps)<{ __active: boolean }>`
  padding: 4px 12px;
  overflow: hidden;
  border-radius: 4px;
  :hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${props =>
    props.__active &&
    `
      background: rgba(255, 255, 255, 0.10);
    `}
`

const CurrentPriceContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CurrentTokenText = styled('span')`
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-left: 8px;
`

const CurrentPrice = styled(Typography)`
  color: #fff;
  font-size: 24px;
  line-height: 1.2;

  ${props => props.theme.breakpoints.up('res768')} {
    font-size: 32px;
  }
`

const USDPriceText = styled(PriceText)`
  color: #b1b3b3;
  font-size: 16px;
`

const FlexContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 8px;
  ${props => props.theme.breakpoints.down('res480')} {
    flex-direction: column-reverse;
  }
`

const BottomContainer = styled(Box)`
  padding: 12px 0px 12px 16px;
`

const DetailRateButton = styled(ButtonBase)`
  display: flex;
  gap: 4px;
  padding: 0px 4px;
  color: #999;

  align-self: center;
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: flex-end;
  }
`

const StyledVisibilityIcon = styled(VisibilityIcon)`
  width: 16px;
`

const StyledVisibilityOffIcon = styled(VisibilityOffIcon)`
  width: 16px;
`

const Placeholder = styled(Box)`
  height: 24px;
`

const SettleOrderTokenSwitcher = ({
  sellOrderTokenType,
  settleOrderTokenType,
  price,
  onTokenTypeChange
}: Props) => {
  const {
    getCurrency,
    convertCurrencyPrice,
    getUSDPrice,
    getApeironDetailPrice
  } = useCurrencyContext()

  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const currentCurrency = getCurrency(settleOrderTokenType)

  const handleToggleDetail = useCallback(() => {
    setOpen(open => !open)
  }, [setOpen])

  const settleOrderTokenPrice = convertCurrencyPrice(
    price,
    sellOrderTokenType,
    settleOrderTokenType
  )

  const usdPrice = getUSDPrice(
    currentCurrency?.type,
    sellOrderTokenType === settleOrderTokenType
      ? settleOrderTokenPrice
      : settleOrderTokenPrice * EXCHANGE_FEE_MULTIPLIER,
    false
  )

  const formattedPrice = getApeironDetailPrice(
    sellOrderTokenType === settleOrderTokenType
      ? price
      : settleOrderTokenPrice * EXCHANGE_FEE_MULTIPLIER,
    Math.min(currentCurrency?.decimals || 18, 18)
  )

  return (
    <Container>
      <SwitcherContainer>
        {OPTIONS.map(option => {
          const currency = getCurrency(option)

          return (
            <SwitchButton
              key={option}
              __active={settleOrderTokenType === option}
              onClick={() => onTokenTypeChange(option)}
            >
              <CurrencyIconImage src={currency?.image} />
            </SwitchButton>
          )
        })}
      </SwitcherContainer>
      <BottomContainer>
        <FlexContainer>
          <CurrentPriceContainer>
            <CurrentCurrencyIconImage src={currentCurrency?.image} />
            <CurrentPrice>
              {formattedPrice}
              <CurrentTokenText>{currentCurrency?.name}</CurrentTokenText>
            </CurrentPrice>
          </CurrentPriceContainer>
          {sellOrderTokenType !== settleOrderTokenType ? (
            <DetailRateButton onClick={handleToggleDetail}>
              {open ? <StyledVisibilityOffIcon /> : <StyledVisibilityIcon />}
              {t('dialog.buy.detail_rate')}
            </DetailRateButton>
          ) : (
            <Placeholder />
          )}
        </FlexContainer>
        <Box>
          <USDPriceText prefix='~$' displayPrice={usdPrice} />
        </Box>
      </BottomContainer>
      {sellOrderTokenType !== settleOrderTokenType && open && (
        <ExchangeRateDetail
          price={settleOrderTokenPrice}
          settleOrderTokenType={settleOrderTokenType}
        />
      )}
    </Container>
  )
}

type Props = {
  sellOrderTokenType: CurrencyTokenType
  price: number
  settleOrderTokenType: CurrencySettleOrderTokenType
  onTokenTypeChange: (tokenType: CurrencySettleOrderTokenType) => void
}

export default SettleOrderTokenSwitcher
